import { EventEmitter, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  ChannelEnum,
  ChannelRequestClient,
  ChannelRequestViewModel,
  CreateChannelRequestRequestModel,
  CreateChannelRequestResponseModel,
  DealerViewModel,
  IUpdateChannelRequestRequestModel,
  ModelViewModel,
  PartyRequestModel,
  PartyTypeEnum,
  UpdateChannelRequestRequestModel,
  UpdateChannelRequestResponseModel,
  VehicleRequestModel,
  VehicleViewModel,
} from "src/domain/client";
import { CookieKeys } from "src/domain/constants";
import { TOAST_TYPE } from "src/domain/extentions/toast";
import { BrandService } from "./brand.service";
import { ChassiService } from "./chassi.service";
import { CookieService } from "./cookie.service";
import { ModelAndDealerService } from "./model-and-dealer.service";
import { RedirectBrandService } from "./redirect-brand.service";
import { ShoppingCartService } from "./shopping-cart.service";
import { ThemeService } from "./theme.service";
import { ToastService } from "./toast.service";
import { Location } from "@angular/common";
import { TrackingService } from "./tracking.service";
import {
  DealerSelectionMethod,
  DealerUpdateEventName,
  VehicleUpdateEventName,
} from "src/domain/trackingEnums";
import { ErrorReporterService } from "./error-reporter.service";
import { SilentError } from "src/errors";

@Injectable({
  providedIn: "root",
})
export class ChannelRequestService {
  channelRequestUpdated$ = new EventEmitter<ChannelRequestViewModel>();

  channelRequest?: ChannelRequestViewModel;

  constructor(
    private cookieService: CookieService,
    private brandService: BrandService,
    private shoppingCartService: ShoppingCartService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modelAndDealerService: ModelAndDealerService,
    private chassiService: ChassiService,
    private themeService: ThemeService,
    private redirectBrandService: RedirectBrandService,
    private location: Location,
    private trackingService: TrackingService,
    private errorReporter: ErrorReporterService,
  ) {}

  get showModelName(): boolean {
    return (
      !!this.channelRequest &&
      !!this.channelRequest.shoppingCart &&
      !!this.channelRequest.shoppingCart.vehicle &&
      !!this.channelRequest.shoppingCart.vehicle.displayName &&
      !this.channelRequest.shoppingCart.vehicle.licensePlate
    );
  }

  initiateChannelRequest(
    dealerNumber = "",
    regNr = "",
  ): Promise<ChannelRequestViewModel> {
    const existingChannelRequestId = this.cookieService.getCookie(
      CookieKeys.channelRequestId,
    );

    if (!!existingChannelRequestId) {
      return this.getChannelRequest(existingChannelRequestId).catch((err) => {
        this.errorReporter.handle(err);
        this.cookieService.deleteCookie(CookieKeys.channelRequestId);
        return this.createChannelRequest(dealerNumber, regNr);
      });
    } else {
      return this.createChannelRequest(dealerNumber, regNr);
    }
  }

  setChannelRequest(channelRequest: ChannelRequestViewModel | undefined): void {
    this.setChannelRequestIdCookie(channelRequest?.channelRequestId ?? null);
    this.channelRequest = channelRequest;
    this.shoppingCartService.setShoppingCart(channelRequest?.shoppingCart);
  }

  updateChannelRequest(
    regNr?: string | null,
    dealer?: DealerViewModel | null,
    dealerSelectionMethod?: DealerSelectionMethod | null,
    model?: ModelViewModel | null,
    showCarAndDealerMenuToast = true,
    reset = false,
  ): Promise<void> {
    // create new promise for updating channel request
    return new Promise<void>(async (resolve, reject) => {
      // setting undefined is allowed if every arg is undefined regNr, model, dealer and will reset the channelRequest.
      if (reset || (!regNr && !dealer && !model)) {
        this.updateChannelRequestPut(
          resolve,
          reject,
          regNr,
          dealer,
          dealerSelectionMethod,
          model,
          showCarAndDealerMenuToast,
        );
      } else {
        dealer = dealer || this.modelAndDealerService.selectedDealer;

        if (!regNr && !model) {
          regNr = this.channelRequest?.shoppingCart?.vehicle?.licensePlate;

          if (this.channelRequest?.shoppingCart?.vehicle) {
            model = new ModelViewModel({
              year: this.channelRequest.shoppingCart.vehicle.year,
              modelCode: this.channelRequest.shoppingCart.vehicle.modelCode,
            });
          }
        }
        if (regNr) {
          // regNr is provided, do a chassi lookup to know if we need to redirect the user to a different brand site or not
          this.chassiService
            .getChassiByRegNr(regNr)
            .then((chassi) => {
              // the chassi was of current brand site, do the updating of the channel request
              if (
                (chassi.brand?.urlShort === "seat" &&
                  this.themeService.brand === "cupra") ||
                chassi.brand?.urlShort === this.themeService.brand
              ) {
                this.updateChannelRequestPut(
                  resolve,
                  reject,
                  regNr,
                  dealer,
                  dealerSelectionMethod,
                  model,
                  showCarAndDealerMenuToast,
                );
              }
              // we need to redirect
              else {
                this.redirectBrandService.openRedirectBrandComponent(chassi);
                resolve();
              }
            })
            .catch((err) => {
              this.errorReporter.chassiNotFound(regNr, err);
              reject(new SilentError(err));
            });
        }
        // regNr is not provided, update the channel request with the dealer
        else {
          this.updateChannelRequestPut(
            resolve,
            reject,
            regNr,
            dealer,
            dealerSelectionMethod,
            model,
            showCarAndDealerMenuToast,
          );
        }
      }
    });
  }

  createRequestRequestModel(
    data?: Omit<
      IUpdateChannelRequestRequestModel,
      "channelRequestId" | "expirationDate" | "externalSystemReference"
    >,
  ) {
    const channelRequestId = this.channelRequest?.channelRequestId;
    const expirationDate = this.channelRequest?.expires;
    if (channelRequestId == null) {
      throw new Error("Cannot update channelRequest, missing channelRequestId");
    }
    if (expirationDate == null) {
      throw new Error("Cannot update channelRequest, missing expirationDate");
    }
    return new UpdateChannelRequestRequestModel({
      channelRequestId,
      expirationDate,
      externalSystemReference: this.channelRequest?.externalSystemReference,
      ...data,
    });
  }

  updateChannelRequestCustomerParty(
    customerPartyRequestModel: PartyRequestModel,
  ): Promise<UpdateChannelRequestResponseModel> {
    const requestModel = this.createRequestRequestModel({
      prepare: this.channelRequest?.prepare,
      parties: [customerPartyRequestModel],
    });
    const selectedDealer = this.modelAndDealerService.selectedDealer;
    if (selectedDealer && selectedDealer.name != null) {
      requestModel?.parties?.push(
        new PartyRequestModel({
          partyType: PartyTypeEnum.Dealer,
          identity: selectedDealer.number,
          name1: selectedDealer.name,
        }),
      );
    }

    if (this.channelRequest?.shoppingCart?.vehicle) {
      requestModel.vehicle = new VehicleRequestModel({
        licensePlate: this.channelRequest.shoppingCart.vehicle.licensePlate,
        vin: this.channelRequest.shoppingCart.vehicle.vin,
        modelCode: this.channelRequest.shoppingCart.vehicle.modelCode,
        year: this.channelRequest.shoppingCart.vehicle.year,
        colorCode: this.channelRequest.shoppingCart.vehicle.colorCode,
        upholsteryCode: this.channelRequest.shoppingCart.vehicle.upholsteryCode,
      });
    }

    return new ChannelRequestClient().update(requestModel);
  }

  //////////

  private updateChannelRequestPut(
    resolve: (value: void | PromiseLike<void>) => void,
    reject: (reason?: any) => void,
    regNr?: string | null,
    dealer?: DealerViewModel | null,
    dealerSelectionMethod?: DealerSelectionMethod | null,
    model?: ModelViewModel | null,
    carAndDealerMenuToast = true,
  ): void {
    const channelRequestId = this.channelRequest?.channelRequestId;
    const expirationDate = this.channelRequest?.expires;
    if (channelRequestId == null) {
      throw new Error("Cannot update channelRequest, missing channelRequestId");
    }
    if (expirationDate == null) {
      throw new Error("Cannot update channelRequest, missing expirationDate");
    }
    const requestModel = this.createRequestRequestModel({
      prepare: this.channelRequest?.prepare,
      parties: [],
      setup: [],
    });

    const currentDealer = this.channelRequest?.parties?.find(
      (_) => _.partyTypeName === PartyTypeEnum.Dealer,
    );
    let dealerChanged = false;
    if (dealer && dealer.name != null) {
      requestModel.parties = [
        new PartyRequestModel({
          partyType: PartyTypeEnum.Dealer,
          identity: dealer.number,
          name1: dealer.name,
        }),
      ];
      dealerChanged = currentDealer?.identity !== dealer.number;
    }

    const hadVehicle = this.channelRequest?.shoppingCart?.vehicle != null;
    let vehicleChanged = false;

    if (regNr) {
      vehicleChanged =
        this.channelRequest?.shoppingCart?.vehicle?.licensePlate?.toLocaleLowerCase() !==
        regNr?.toLocaleLowerCase();
      requestModel.vehicle = new VehicleRequestModel({
        licensePlate: regNr,
      });
    } else if (model) {
      vehicleChanged =
        this.channelRequest?.shoppingCart?.vehicle?.year !== model.year ||
        this.channelRequest?.shoppingCart?.vehicle?.modelCode !==
          model.modelCode;

      requestModel.vehicle = new VehicleRequestModel({
        year: model.year,
        modelCode: model.modelCode,
      });
    }
    new ChannelRequestClient()
      .update(requestModel)
      .then(() => {
        this.updateChannelRequestSuccess({
          dealerSelected: dealerChanged && currentDealer == null,
          dealerChanged: dealerChanged && currentDealer != null,
          vehicleSelected: vehicleChanged && !hadVehicle,
          vehicleChanged: vehicleChanged && hadVehicle,
          dealer,
          dealerSelectionMethod,
          carAndDealerMenuToast,
        }).finally(resolve);
      })
      .catch(reject);
  }

  private trackVehicleChange(newVehicle: VehicleViewModel, selected: boolean) {
    if (
      newVehicle &&
      (this.brandService.brandId === newVehicle.brandId ||
        this.brandService.specialBrandId === newVehicle.brandId)
    ) {
      const brand = this.brandService.resoveBrandSync(newVehicle.brandId);
      const model =
        newVehicle.modelCode != null &&
        this.modelAndDealerService.selectedModel?.modelCode ===
          newVehicle.modelCode
          ? this.modelAndDealerService.selectedModel
          : null;
      if (
        model?.name != null &&
        brand?.urlShort != null &&
        model.year != null
      ) {
        this.trackingService.trackUpdateVehicle(
          selected
            ? VehicleUpdateEventName.SELECT
            : VehicleUpdateEventName.CHANGE,
          !!newVehicle.licensePlate,
          model.name,
          brand.urlShort,
          model.year,
        );
      }
    }
  }

  private trackDealerChange(
    newDealer: DealerViewModel,
    selectionMethod: DealerSelectionMethod,
    selected: boolean,
  ) {
    if (newDealer) {
      this.trackingService.trackUpdateDealer(
        selected ? DealerUpdateEventName.SELECT : DealerUpdateEventName.CHANGE,
        newDealer,
        selectionMethod,
      );
    }
  }

  private getChannelRequest(
    channelRequestId: string | undefined,
  ): Promise<ChannelRequestViewModel> {
    return new ChannelRequestClient()
      .channelRequestGet(channelRequestId, undefined)
      .then((channelRequest: ChannelRequestViewModel) => {
        this.channelRequest = channelRequest;
        this.shoppingCartService.setShoppingCart(channelRequest.shoppingCart);

        return channelRequest;
      });
  }

  createChannelRequest(
    dealerNumber = "",
    regNr = "",
  ): Promise<ChannelRequestViewModel> {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 90);

    const requestModel = new CreateChannelRequestRequestModel({
      channel: ChannelEnum.TBO,
      expirationDate,
      brandCode: this.brandService.brand?.brandCode,
      setup: [],
      parties: [],
    });

    if (dealerNumber) {
      requestModel.parties = requestModel.parties ?? [];
      requestModel.parties.push(
        new PartyRequestModel({
          identity: dealerNumber,
          partyType: PartyTypeEnum.Dealer,
          name1: dealerNumber,
        }),
      );
    }

    if (regNr) {
      requestModel.vehicle = new VehicleRequestModel({
        licensePlate: regNr,
      });
    }

    return new ChannelRequestClient()
      .channelRequestPost(requestModel)
      .then((response: CreateChannelRequestResponseModel) => {
        this.setChannelRequestIdCookie(response.channelRequestId);
        return this.getChannelRequest(response.channelRequestId);
      });
  }

  private async updateChannelRequestSuccess({
    dealerSelected,
    dealerChanged,
    vehicleSelected,
    vehicleChanged,
    dealer = null,
    dealerSelectionMethod,
    carAndDealerMenuToast = true,
  }: {
    dealerSelected: boolean;
    dealerChanged: boolean;
    vehicleSelected: boolean;
    vehicleChanged: boolean;
    dealer?: DealerViewModel | null;
    dealerSelectionMethod?: DealerSelectionMethod | null;
    carAndDealerMenuToast?: boolean | null;
  }): Promise<void> {
    if (this.channelRequest?.channelRequestId == null) {
      throw new Error("No channel request id set");
    }
    try {
      const { shoppingCart } = await this.getChannelRequest(
        this.channelRequest?.channelRequestId,
      );
      if (
        shoppingCart != null &&
        shoppingCart.vehicle?.modelCode != null &&
        shoppingCart.vehicle?.year != null
      ) {
        const { modelCode, year } = shoppingCart.vehicle;
        const { selectedModel } = this.modelAndDealerService;
        if (
          !selectedModel ||
          selectedModel.modelCode !== modelCode ||
          selectedModel.year !== year
        ) {
          this.modelAndDealerService.setSelectedModel(
            await this.modelAndDealerService.getModelByCodeAndYear(
              shoppingCart.vehicle.modelCode,
              shoppingCart.vehicle.year,
            ),
          );
        }
      } else {
        this.modelAndDealerService.setSelectedModel(null);
      }
      this.modelAndDealerService.setSelectedDealer(dealer);
      if (vehicleSelected && shoppingCart?.vehicle != null)
        this.trackVehicleChange(shoppingCart?.vehicle, true);
      if (vehicleChanged && shoppingCart?.vehicle != null)
        this.trackVehicleChange(shoppingCart?.vehicle, false);
      if (dealerSelected && dealer != null && dealerSelectionMethod != null)
        this.trackDealerChange(dealer, dealerSelectionMethod, true);
      if (dealerChanged && dealer != null && dealerSelectionMethod != null)
        this.trackDealerChange(dealer, dealerSelectionMethod, false);
    } finally {
      if (!dealerChanged && !vehicleChanged && carAndDealerMenuToast) {
        this.toastService.createToast(
          this.translateService.instant("carAndDealer.carAndDealerMenuToast"),
          TOAST_TYPE.INFO,
        );
      }

      if (dealerChanged) {
        this.toastService.createTopBannerMessage(
          this.translateService.instant(
            "topBannerMessages.changedDealer.title",
          ),
          this.translateService.instant("topBannerMessages.changedDealer.text"),
          false,
        );
      }

      if (vehicleChanged) {
        this.toastService.createTopBannerMessage(
          this.translateService.instant(
            "topBannerMessages.changedVehicle.title",
          ),
          this.translateService.instant(
            "topBannerMessages.changedVehicle.text",
          ),
          false,
        );
      }

      this.channelRequestUpdated$.emit(this.channelRequest);
    }
  }

  private setChannelRequestIdCookie(
    channelRequestId: string | null | undefined,
  ): void {
    if (!channelRequestId) {
      this.cookieService.deleteCookie(
        CookieKeys.channelRequestId,
        `/${this.location.path().split("/")[1]}`,
      );
    } else {
      this.cookieService.setCookie(
        CookieKeys.channelRequestId,
        channelRequestId,
        90,
        `/${this.location.path().split("/")[1]}`,
      );
    }
  }
}
